// src/components/leitner/TaskShareModal.jsx
import React, { useState } from 'react';
import { 
  X, Copy, Check, Link as LinkIcon, Share2, Send, Mail, 
  MessageCircle, Facebook, Twitter, Globe, Users, Lock,
  Image, Upload, Trash2, ExternalLink
} from 'lucide-react';
import { taskService } from '../../services/taskService';

const TaskShareModal = ({ task, bookId, bookData, onClose, onUpdate }) => {
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('share'); // 'share', 'assign', 'meta'
  const [emailInput, setEmailInput] = useState('');
  const [assignedUsers, setAssignedUsers] = useState(task.assignedUsers || []);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const [metaData, setMetaData] = useState({
    ogTitle: task.ogTitle || task.name,
    ogDescription: task.ogDescription || task.description || '',
    ogImage: task.ogImage || null
  });

  const getShareUrl = () => {
    const customDomain = bookData?.customDomain;
    const customPath = bookData?.customPath || bookData?.path;
    
    if (customDomain) {
      // Custom domain with path: parrotbooks.co.za/leitner/tasks/taskId
      // Custom domain without path: showcase.africa/tasks/taskId
      const basePath = customPath ? `/${customPath}` : '';
      return `https://${customDomain}${basePath}/tasks/${task.id}`;
    }
    
    // Fallback to standard URL
    return `${window.location.origin}/books/${bookId}/tasks/${task.id}`;
  };

  const shareUrl = getShareUrl();

  // ... rest of component unchanged

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleNativeShare = async () => {
    try {
      await navigator.share({
        title: metaData.ogTitle,
        text: metaData.ogDescription,
        url: shareUrl
      });
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Share failed:', error);
      }
    }
  };

  const shareToSocial = (platform) => {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(metaData.ogTitle);
    const encodedDesc = encodeURIComponent(metaData.ogDescription);
    
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      email: `mailto:?subject=${encodedTitle}&body=${encodedDesc}%0A%0A${encodedUrl}`
    };
    
    if (platform === 'email') {
      window.location.href = urls[platform];
    } else {
      window.open(urls[platform], '_blank', 'width=600,height=400');
    }
  };

  const handleAddEmail = () => {
    const email = emailInput.trim().toLowerCase();
    if (email && email.includes('@') && !assignedUsers.includes(email)) {
      setAssignedUsers(prev => [...prev, email]);
      setEmailInput('');
    }
  };

  const handleRemoveEmail = (email) => {
    setAssignedUsers(prev => prev.filter(e => e !== email));
  };

  const handleSaveAssignments = async () => {
    setSaving(true);
    try {
      await taskService.updateTask(bookId, task.id, {
        assignedUsers,
        visibility: task.visibility
      });
      onUpdate({ assignedUsers });
    } catch (error) {
      console.error('Failed to save assignments:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMeta = async () => {
    setSaving(true);
    try {
      await taskService.updateTask(bookId, task.id, metaData);
      onUpdate(metaData);
    } catch (error) {
      console.error('Failed to save meta:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const url = await taskService.uploadTaskImage(bookId, task.id, file);
      setMetaData(prev => ({ ...prev, ogImage: url }));
    } catch (error) {
      console.error('Failed to upload image:', error);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = async () => {
    setMetaData(prev => ({ ...prev, ogImage: null }));
    await taskService.updateTask(bookId, task.id, { ogImage: null });
  };

  const getVisibilityInfo = () => {
    switch (task.visibility) {
      case 'public':
        return { icon: Globe, label: 'Public', desc: 'Anyone with the link can access' };
      case 'organisation':
        return { icon: Users, label: 'Organisation', desc: 'All organization members can access' };
      default:
        return { icon: Lock, label: 'Private', desc: 'Only assigned users can access' };
    }
  };

  const visibilityInfo = getVisibilityInfo();

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Share2 className="h-5 w-5 text-indigo-400" />
            Share Task
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-slate-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-700">
          {[
            { id: 'share', label: 'Share Link' },
            { id: 'assign', label: 'Assign Users' },
            { id: 'meta', label: 'Preview' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-indigo-400 border-b-2 border-indigo-400'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {activeTab === 'share' && (
            <div className="space-y-4">
              {/* Task Preview */}
              <div className="flex items-center gap-4 p-4 bg-slate-900 rounded-lg">
                {metaData.ogImage ? (
                  <img 
                    src={metaData.ogImage}
                    alt={task.name}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                ) : (
                  <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <LinkIcon className="h-8 w-8 text-white" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-white truncate">{task.name}</h4>
                  <p className="text-sm text-slate-400 mt-1">
                    {task.questionIds?.length || 0} questions
                  </p>
                  <div className="flex items-center gap-1 mt-2 text-xs">
                    <visibilityInfo.icon className="h-3 w-3 text-slate-400" />
                    <span className="text-slate-400">{visibilityInfo.label}</span>
                  </div>
                </div>
              </div>

              {/* Share Link */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300">Share Link</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm"
                  />
                  <button
                    onClick={handleCopy}
                    className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                      copied
                        ? 'bg-green-500 text-white'
                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                    }`}
                  >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Native Share */}
              {navigator.share && (
                <button
                  onClick={handleNativeShare}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                >
                  <Send className="h-4 w-4" />
                  Share via Device
                </button>
              )}

              {/* Social Share */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { id: 'facebook', icon: Facebook, label: 'Facebook', color: 'text-blue-500' },
                  { id: 'twitter', icon: Twitter, label: 'Twitter', color: 'text-sky-400' },
                  { id: 'whatsapp', icon: MessageCircle, label: 'WhatsApp', color: 'text-green-500' },
                  { id: 'email', icon: Mail, label: 'Email', color: 'text-slate-400' }
                ].map(({ id, icon: Icon, label, color }) => (
                  <button
                    key={id}
                    onClick={() => shareToSocial(id)}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                  >
                    <Icon className={`h-4 w-4 ${color}`} />
                    <span className="text-sm text-white font-medium">{label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'assign' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-400">
                Add users by email to give them access to this task.
              </p>

              {/* Add Email */}
              <div className="flex gap-2">
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddEmail()}
                  placeholder="Enter email address..."
                  className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500"
                />
                <button
                  onClick={handleAddEmail}
                  disabled={!emailInput.includes('@')}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                >
                  Add
                </button>
              </div>

              {/* Assigned Users List */}
              {assignedUsers.length > 0 ? (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300">
                    Assigned Users ({assignedUsers.length})
                  </label>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {assignedUsers.map(email => (
                      <div
                        key={email}
                        className="flex items-center justify-between px-3 py-2 bg-slate-900 rounded-lg"
                      >
                        <span className="text-sm text-white truncate">{email}</span>
                        <button
                          onClick={() => handleRemoveEmail(email)}
                          className="p-1 hover:bg-slate-700 rounded transition-colors"
                        >
                          <X className="h-4 w-4 text-slate-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Users className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No users assigned yet</p>
                </div>
              )}

              {/* Save Button */}
              <button
                onClick={handleSaveAssignments}
                disabled={saving}
                className="w-full py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Assignments'}
              </button>
            </div>
          )}

          {activeTab === 'meta' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-400">
                Customize how this task appears when shared on social media.
              </p>

              {/* OG Image */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300">Share Image</label>
                {metaData.ogImage ? (
                  <div className="relative">
                    <img
                      src={metaData.ogImage}
                      alt="Share preview"
                      className="w-full h-40 object-cover rounded-lg"
                    />
                    <button
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 p-2 bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
                    >
                      <Trash2 className="h-4 w-4 text-white" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-40 border-2 border-dashed border-slate-600 rounded-lg cursor-pointer hover:border-indigo-500 transition-colors">
                    {uploadingImage ? (
                      <div className="animate-spin h-8 w-8 border-2 border-indigo-500 border-t-transparent rounded-full" />
                    ) : (
                      <>
                        <Upload className="h-8 w-8 text-slate-400 mb-2" />
                        <span className="text-sm text-slate-400">Click to upload image</span>
                        <span className="text-xs text-slate-500 mt-1">1200x630px recommended</span>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                )}
                <p className="text-xs text-slate-500">
                  If not set, the book's universal OG image will be used.
                </p>
              </div>

              {/* OG Title */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300">Share Title</label>
                <input
                  type="text"
                  value={metaData.ogTitle}
                  onChange={(e) => setMetaData(prev => ({ ...prev, ogTitle: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm"
                  placeholder={task.name}
                />
              </div>

              {/* OG Description */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300">Share Description</label>
                <textarea
                  value={metaData.ogDescription}
                  onChange={(e) => setMetaData(prev => ({ ...prev, ogDescription: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm resize-none"
                  rows={3}
                  placeholder="Describe this task..."
                />
              </div>

              {/* Preview Card */}
              <div className="p-4 bg-slate-900 rounded-lg">
                <div className="text-xs text-slate-500 mb-2">Preview</div>
                <div className="border border-slate-700 rounded-lg overflow-hidden">
                  {metaData.ogImage && (
                    <img
                      src={metaData.ogImage}
                      alt="Preview"
                      className="w-full h-32 object-cover"
                    />
                  )}
                  <div className="p-3">
                    <h4 className="font-medium text-white text-sm truncate">
                      {metaData.ogTitle || task.name}
                    </h4>
                    <p className="text-xs text-slate-400 mt-1 line-clamp-2">
                      {metaData.ogDescription || 'No description'}
                    </p>
                    <p className="text-xs text-slate-500 mt-2 truncate">
                      {window.location.host}
                    </p>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <button
                onClick={handleSaveMeta}
                disabled={saving}
                className="w-full py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Preview Settings'}
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-700 bg-slate-800/50">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <visibilityInfo.icon className="h-4 w-4" />
            <span>{visibilityInfo.desc}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskShareModal;